import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { head, prop, find, propEq, filter, isEmpty } from 'ramda';
import { useTranslation } from 'react-i18next';
import { isBlank } from 'utils/helperMethods';
import PropTypes from 'prop-types';
import { Box, Typography, TextField, Button } from '@material-ui/core';
import Loader from 'sharedComponents/Loader';
import PlacesAutocomplete, { geocodeByAddress } from 'lib/react-places-autocomplete';
import MortgageSurveyQuestionPresenter from 'presenters/MortgageSurveyQuestionPresenter';

import useStyles from './useStyles';

const TYPE_MAPPER = [
  {
    field: 'city',
    componentType: 'locality',
    componentField: 'long_name',
  },
  {
    field: 'state',
    componentType: 'administrative_area_level_1',
    componentField: 'short_name',
  },
];

const buildLocationString = fields => {
  const { value: city } = find(propEq('field', 'city'))(fields);
  const { value: state } = find(propEq('field', 'state'))(fields);
  return filter(value => !isBlank(value), [city, state]).join(', ');
};

const LocationQuestion = props => {
  const { question, onChange, onValidate, onNext, isValid } = props;
  const { t } = useTranslation();
  const classes = useStyles();

  useEffect(() => {
    onValidate(false);
  }, []);

  const [inputAddress, setInputAddress] = useState('');
  const [error, setError] = useState('');

  const handleChange = address => {
    setInputAddress(address);
    onValidate(false);
  };

  const handleSelect = async address => {
    const fieldValues = [];
    try {
      const response = await geocodeByAddress(address);
      const components = prop('address_components', head(response));
      components.forEach(component => {
        const type = head(component.types);
        const mapper = find(propEq('componentType', type))(TYPE_MAPPER);
        if (mapper) {
          const { field, componentField } = mapper;
          fieldValues.push({ field, value: component[componentField] });
        }
      });

      const locationString = buildLocationString(fieldValues);
      setInputAddress(locationString);
      onChange([...fieldValues, { field: 'locationString', value: locationString }]);
      onValidate(true);
    } catch (err) {
      setError(err);
    }
  };

  return (
    <Box>
      <Typography variant="h1">{MortgageSurveyQuestionPresenter.label(question)}</Typography>
      <Box mt={6} />
      <Box display="flex" width="100%" my={3}>
        <PlacesAutocomplete
          value={inputAddress}
          onChange={handleChange}
          onSelect={handleSelect}
          searchOptions={{
            componentRestrictions: { country: 'us' },
            types: ['(cities)'],
          }}
          onError={() => {}}
          clearItemsOnError
        >
          {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
            <Box className={classes.inputContainer}>
              <TextField
                className={classes.input}
                variant="outlined"
                {...getInputProps({
                  placeholder: t('components.MortgageSurvey.LocationQuestion.placeholder'),
                })}
              />
              {isValid && (
                <Button onClick={onNext} className={classes.button} variant="contained" disabled={!isValid}>
                  {t('components.MortgageSurvey.ControlBar.nextButton')}
                </Button>
              )}
              <Box className={classes.dropdown}>
                {!isEmpty(error) && (
                  <Typography className={classes.error} variant="subtitle">
                    Error
                  </Typography>
                )}
                {loading && <Loader />}
                {suggestions.map(suggestion => {
                  return (
                    <Box
                      {...getSuggestionItemProps(suggestion, {})}
                      key={suggestion.placeId}
                      className={clsx(classes.item, { [classes.active]: suggestion.active })}
                    >
                      <Typography variant="body1">{suggestion.description.replace(', USA', '')}</Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </PlacesAutocomplete>
      </Box>
    </Box>
  );
};

LocationQuestion.propTypes = {
  question: MortgageSurveyQuestionPresenter.shape().isRequired,
  onChange: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
};

export default LocationQuestion;



// WEBPACK FOOTER //
// ./src/sharedComponents/MortgageSurvey/components/LocationQuestion/LocationQuestion.js